/**
 * LineasAdmin - Admin page for managing bus lines, servers and their primas
 */
import { useState, useEffect } from 'react';
import {
    Plus, Edit2, Trash2, Loader2, Save, X, ChevronDown, ChevronUp,
    MapPin, Clock, Award, Bus, Search, Calendar, Truck, Sun, Moon, Zap, Check
} from 'lucide-react';
import { lineas as lineasApi, primas as primasApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

// Day type options
const TIPO_DIA_OPTIONS = [
    { value: 'todos', label: 'Todos los dias', color: 'bg-gray-500/20 text-gray-400' },
    { value: 'L-V', label: 'L-V (Entre semana)', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'S', label: 'Sabados', color: 'bg-orange-500/20 text-orange-400' },
    { value: 'D', label: 'Domingos/Festivos', color: 'bg-purple-500/20 text-purple-400' },
];

export default function LineasAdmin() {
    const { success, error: toastError } = useToast();
    const [lineas, setLineas] = useState([]);
    const [primas, setPrimas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedLinea, setExpandedLinea] = useState(null);
    const [servidoresPorLinea, setServidoresPorLinea] = useState({});
    const [expandedServerTypes, setExpandedServerTypes] = useState({}); // { "lineaId-tipoDia": boolean }

    // Modal states
    const [showLineaModal, setShowLineaModal] = useState(false);
    const [showServidorModal, setShowServidorModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [editingLinea, setEditingLinea] = useState(null);
    const [editingServidor, setEditingServidor] = useState(null);
    const [saving, setSaving] = useState(false);

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, id: null, lineaId: null });

    // Form states
    const [lineaForm, setLineaForm] = useState({ codigo: '', nombre: '', descripcion: '' });
    const [servidorForm, setServidorForm] = useState({
        linea_id: null, codigo: '', nombre: '',
        hora_inicio1: '', hora_fin1: '',
        hora_inicio2: '', hora_fin2: '',
        tipo_dia: 'todos', reparto: false, primas_config: []
    });
    const [bulkForm, setBulkForm] = useState({
        linea_ids: [], tipo_dia: 'L-V', cantidad: 6, codigo_prefijo: '',
        hora_inicio1: '', hora_fin1: '',
        hora_inicio2: '', hora_fin2: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [lineasData, primasData] = await Promise.all([
                lineasApi.getAllAdmin(),
                primasApi.getAllAdmin()
            ]);
            setLineas(lineasData);
            setPrimas(primasData.filter(p => !p.es_global)); // Only server-specific primas
        } catch (err) {
            console.error('Error loading data:', err);
            toastError('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const loadServidores = async (lineaId) => {
        try {
            const data = await lineasApi.getServidores(lineaId);
            setServidoresPorLinea(prev => ({ ...prev, [lineaId]: data }));
        } catch (err) {
            console.error('Error loading servidores:', err);
        }
    };

    const toggleExpand = async (lineaId) => {
        if (expandedLinea === lineaId) {
            setExpandedLinea(null);
        } else {
            setExpandedLinea(lineaId);
            if (!servidoresPorLinea[lineaId]) {
                await loadServidores(lineaId);
            }
        }
    };

    // === LINEA CRUD ===
    const openLineaModal = (linea = null) => {
        if (linea) {
            setEditingLinea(linea);
            setLineaForm({ codigo: linea.codigo, nombre: linea.nombre, descripcion: linea.descripcion || '' });
        } else {
            setEditingLinea(null);
            setLineaForm({ codigo: '', nombre: '', descripcion: '' });
        }
        setShowLineaModal(true);
    };

    const saveLinea = async () => {
        if (!lineaForm.codigo || !lineaForm.nombre) {
            toastError('Codigo y nombre son requeridos');
            return;
        }
        try {
            setSaving(true);
            if (editingLinea) {
                await lineasApi.update(editingLinea.id, lineaForm);
                success('Linea actualizada');
            } else {
                await lineasApi.create(lineaForm);
                success('Linea creada');
            }
            setShowLineaModal(false);
            loadData();
        } catch (err) {
            toastError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const openDeleteLineaModal = (id) => {
        setConfirmModal({ isOpen: true, type: 'linea', id, lineaId: null });
    };

    const deleteLinea = async (id) => {
        try {
            await lineasApi.delete(id);
            success('Linea eliminada');
            loadData();
        } catch (err) {
            toastError(err.message);
        }
    };

    const openServidorModal = (lineaId, servidor = null) => {
        if (servidor) {
            setEditingServidor(servidor);
            // Convert existing primas to primas_config format (including turno)
            const primasConfig = servidor.primas?.map(p => {
                const prima = primas.find(pr => pr.codigo === p.codigo);
                return prima ? { prima_id: prima.id, turno: p.turno || 'A' } : null;
            }).filter(Boolean) || [];
            setServidorForm({
                linea_id: lineaId,
                codigo: servidor.codigo,
                nombre: servidor.nombre || '',
                hora_inicio1: servidor.hora_inicio1?.slice(0, 5) || '',
                hora_fin1: servidor.hora_fin1?.slice(0, 5) || '',
                hora_inicio2: servidor.hora_inicio2?.slice(0, 5) || '',
                hora_fin2: servidor.hora_fin2?.slice(0, 5) || '',
                tipo_dia: servidor.tipo_dia || 'todos',
                reparto: servidor.reparto || false,
                primas_config: primasConfig
            });
        } else {
            setEditingServidor(null);
            setServidorForm({
                linea_id: lineaId,
                codigo: '',
                nombre: '',
                hora_inicio1: '',
                hora_fin1: '',
                hora_inicio2: '',
                hora_fin2: '',
                tipo_dia: 'todos',
                reparto: false,
                primas_config: []
            });
        }
        setShowServidorModal(true);
    };

    const saveServidor = async () => {
        if (!servidorForm.codigo) {
            toastError('El codigo es requerido');
            return;
        }
        try {
            setSaving(true);
            if (editingServidor) {
                await lineasApi.updateServidor(editingServidor.id, servidorForm);
                success('Servidor actualizado');
            } else {
                await lineasApi.createServidor(servidorForm);
                success('Servidor creado');
            }
            setShowServidorModal(false);
            loadServidores(servidorForm.linea_id);
        } catch (err) {
            toastError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const openDeleteServidorModal = (id, lineaId) => {
        setConfirmModal({ isOpen: true, type: 'servidor', id, lineaId });
    };

    const deleteServidor = async (id, lineaId) => {
        try {
            await lineasApi.deleteServidor(id);
            success('Servidor eliminado');
            loadServidores(lineaId);
        } catch (err) {
            toastError(err.message);
        }
    };

    const handleConfirmDelete = () => {
        if (confirmModal.type === 'linea') {
            deleteLinea(confirmModal.id);
        } else if (confirmModal.type === 'servidor') {
            deleteServidor(confirmModal.id, confirmModal.lineaId);
        }
        setConfirmModal({ isOpen: false, type: null, id: null, lineaId: null });
    };

    // Toggle prima for a specific turno (M, T, or null for both)
    const togglePrimaConfig = (primaId, turno) => {
        setServidorForm(prev => {
            const existingIndex = prev.primas_config.findIndex(
                p => p.prima_id === primaId && p.turno === turno
            );
            if (existingIndex >= 0) {
                // Remove it
                return {
                    ...prev,
                    primas_config: prev.primas_config.filter((_, i) => i !== existingIndex)
                };
            } else {
                // Add it
                return {
                    ...prev,
                    primas_config: [...prev.primas_config, { prima_id: primaId, turno }]
                };
            }
        });
    };

    const toggleServerTypeExpand = (lineaId, type) => {
        const key = `${lineaId}-${type}`;
        setExpandedServerTypes(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Check if a prima is selected for a turno
    const isPrimaSelected = (primaId, turno) => {
        return servidorForm.primas_config.some(p => p.prima_id === primaId && p.turno === turno);
    };

    // === BULK CREATION ===
    const toggleLineaSelection = (lineaId) => {
        setBulkForm(prev => ({
            ...prev,
            linea_ids: prev.linea_ids.includes(lineaId)
                ? prev.linea_ids.filter(id => id !== lineaId)
                : [...prev.linea_ids, lineaId]
        }));
    };

    const selectAllLineas = () => {
        setBulkForm(prev => ({
            ...prev,
            linea_ids: prev.linea_ids.length === lineas.length ? [] : lineas.map(l => l.id)
        }));
    };

    const saveBulk = async () => {
        if (bulkForm.linea_ids.length === 0) {
            toastError('Selecciona al menos una línea');
            return;
        }
        if (bulkForm.cantidad < 1 || bulkForm.cantidad > 50) {
            toastError('La cantidad debe ser entre 1 y 50');
            return;
        }
        try {
            setSaving(true);
            const result = await lineasApi.createServidoresBulk(bulkForm);
            success(`${result.total_creados} servidores creados${result.total_omitidos > 0 ? ` (${result.total_omitidos} omitidos por duplicado)` : ''}`);
            setShowBulkModal(false);
            // Reload all affected lines
            for (const lineaId of bulkForm.linea_ids) {
                loadServidores(lineaId);
            }
        } catch (err) {
            toastError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Bus className="w-6 h-6 text-cyan-400" />
                        Líneas y Servidores
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">
                        Gestiona las líneas de autobús y sus servidores
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            setBulkForm({ linea_ids: [], tipo_dia: 'L-V', cantidad: 6, codigo_prefijo: '', hora_inicio1: '', hora_fin1: '', hora_inicio2: '', hora_fin2: '' });
                            setShowBulkModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 font-semibold rounded-lg hover:bg-purple-500/30 transition-all"
                    >
                        <Zap className="w-4 h-4" />
                        Crear Masivo
                    </button>
                    <button
                        onClick={() => openLineaModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-green-500 text-gray-900 font-semibold rounded-lg hover:shadow-lg transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        Nueva Línea
                    </button>
                </div>
            </div>

            {/* Lines list */}
            <div className="space-y-3">
                {lineas.length === 0 ? (
                    <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
                        <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No hay lineas registradas</p>
                        <button
                            onClick={() => openLineaModal()}
                            className="mt-4 text-cyan-400 hover:text-cyan-300"
                        >
                            + Crear primera linea
                        </button>
                    </div>
                ) : (
                    lineas.map(linea => (
                        <div key={linea.id} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                            {/* Linea header */}
                            <div className="flex items-center gap-4 p-4">
                                <button
                                    onClick={() => toggleExpand(linea.id)}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    {expandedLinea === linea.id ? (
                                        <ChevronUp className="w-5 h-5 text-gray-400" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                    )}
                                </button>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 font-bold rounded-lg text-sm">
                                            {linea.codigo}
                                        </span>
                                        <span className="text-white font-medium">{linea.nombre}</span>
                                        {!linea.activa && (
                                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                                Inactiva
                                            </span>
                                        )}
                                    </div>
                                    {linea.descripcion && (
                                        <p className="text-gray-500 text-sm mt-1">{linea.descripcion}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => openLineaModal(linea)}
                                        className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-cyan-400 transition-colors"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => openDeleteLineaModal(linea.id)}
                                        className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Servidores (expanded) */}
                            {expandedLinea === linea.id && (
                                <div className="border-t border-white/10 bg-black/20 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            Servidores
                                        </h3>
                                        <button
                                            onClick={() => openServidorModal(linea.id)}
                                            className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                                        >
                                            <Plus className="w-3 h-3" /> Añadir
                                        </button>
                                    </div>

                                    {!servidoresPorLinea[linea.id] ? (
                                        <div className="flex justify-center py-4">
                                            <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                                        </div>
                                    ) : servidoresPorLinea[linea.id].length === 0 ? (
                                        <p className="text-gray-500 text-sm text-center py-4">
                                            Sin servidores configurados
                                        </p>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* Group by day type */}
                                            {['L-V', 'S', 'D', 'todos'].map(type => {
                                                const serversOfType = servidoresPorLinea[linea.id].filter(
                                                    s => (s.tipo_dia || 'todos') === type
                                                );
                                                if (serversOfType.length === 0) return null;

                                                const groupKey = `${linea.id}-${type}`;
                                                const isExpanded = expandedServerTypes[groupKey] ?? true; // Default open
                                                const typeOption = TIPO_DIA_OPTIONS.find(t => t.value === type);

                                                return (
                                                    <div key={type} className="border border-white/5 rounded-lg overflow-hidden">
                                                        <button
                                                            onClick={() => toggleServerTypeExpand(linea.id, type)}
                                                            className={`w-full flex items-center justify-between p-3 transition-colors ${isExpanded ? 'bg-white/10' : 'bg-white/5 hover:bg-white/10'}`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                                                <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${typeOption?.color || 'text-gray-400'}`}>
                                                                    {typeOption?.label || type}
                                                                </span>
                                                                <span className="text-gray-400 text-xs">
                                                                    ({serversOfType.length})
                                                                </span>
                                                            </div>
                                                        </button>

                                                        {isExpanded && (
                                                            <div className="divide-y divide-white/5">
                                                                {serversOfType.map(srv => (
                                                                    <div key={srv.id} className="flex items-center gap-3 p-3 bg-black/20 hover:bg-black/30 transition-colors">
                                                                        <span className="font-mono text-cyan-400 font-bold min-w-[30px]">{srv.codigo}</span>

                                                                        {srv.reparto && (
                                                                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full flex items-center gap-1">
                                                                                <Truck className="w-3 h-3" /> Reparto
                                                                            </span>
                                                                        )}
                                                                        <div className="flex flex-col text-gray-400 text-xs font-mono">
                                                                            {(srv.hora_inicio1 || srv.hora_fin1) && (
                                                                                <span>M: {srv.hora_inicio1?.slice(0, 5) || '--'} - {srv.hora_fin1?.slice(0, 5) || '--'}</span>
                                                                            )}
                                                                            {(srv.hora_inicio2 || srv.hora_fin2) && (
                                                                                <span>T: {srv.hora_inicio2?.slice(0, 5) || '--'} - {srv.hora_fin2?.slice(0, 5) || '--'}</span>
                                                                            )}
                                                                        </div>
                                                                        {srv.primas && srv.primas.length > 0 && (
                                                                            <div className="flex gap-1 flex-wrap">
                                                                                {srv.primas.map((p, i) => (
                                                                                    <span key={i} className="px-2 py-0.5 bg-green-500/10 text-green-400 text-[10px] rounded-full flex items-center gap-1 border border-green-500/20">
                                                                                        {p.nombre}
                                                                                        {p.turno !== 'A' && (
                                                                                            <span className="opacity-60 text-[8px] font-bold">
                                                                                                ({p.turno})
                                                                                            </span>
                                                                                        )}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        <div className="flex-1" />
                                                                        <button
                                                                            onClick={() => openServidorModal(linea.id, srv)}
                                                                            className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-cyan-400"
                                                                        >
                                                                            <Edit2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => openDeleteServidorModal(srv.id, linea.id)}
                                                                            className="p-1.5 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400"
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Linea Modal */}
            {showLineaModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md p-6">
                        <h2 className="text-xl font-bold text-white mb-4">
                            {editingLinea ? 'Editar Linea' : 'Nueva Linea'}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Codigo</label>
                                <input
                                    type="text"
                                    value={lineaForm.codigo}
                                    onChange={(e) => setLineaForm(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500"
                                    placeholder="Ej: L1, BEI"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Nombre</label>
                                <input
                                    type="text"
                                    value={lineaForm.nombre}
                                    onChange={(e) => setLineaForm(prev => ({ ...prev, nombre: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500"
                                    placeholder="Ej: Linea Centro-Lakua"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Descripcion (opcional)</label>
                                <textarea
                                    value={lineaForm.descripcion}
                                    onChange={(e) => setLineaForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500 resize-none"
                                    rows={2}
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowLineaModal(false)}
                                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={saveLinea}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-green-500 text-gray-900 font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Servidor Modal */}
            {showServidorModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md p-6">
                        <h2 className="text-xl font-bold text-white mb-4">
                            {editingServidor ? 'Editar Servidor' : 'Nuevo Servidor'}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Codigo</label>
                                <input
                                    type="text"
                                    value={servidorForm.codigo}
                                    onChange={(e) => setServidorForm(prev => ({ ...prev, codigo: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cyan-500"
                                    placeholder="Ej: S1, S2, M1"
                                />
                            </div>
                            {/* Turno Mañana */}
                            <div className="grid grid-cols-2 gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                                <div className="col-span-2 text-xs font-bold text-yellow-500/80 mb-1 flex items-center gap-1">
                                    <Sun className="w-3 h-3" /> Turno Mañana
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Hora inicio</label>
                                    <input
                                        type="time"
                                        value={servidorForm.hora_inicio1}
                                        onChange={(e) => setServidorForm(prev => ({ ...prev, hora_inicio1: e.target.value }))}
                                        className="w-full px-3 py-1.5 rounded bg-black/20 border border-white/10 text-white focus:outline-none focus:border-cyan-500 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Hora fin</label>
                                    <input
                                        type="time"
                                        value={servidorForm.hora_fin1}
                                        onChange={(e) => setServidorForm(prev => ({ ...prev, hora_fin1: e.target.value }))}
                                        className="w-full px-3 py-1.5 rounded bg-black/20 border border-white/10 text-white focus:outline-none focus:border-cyan-500 text-sm"
                                    />
                                </div>
                            </div>

                            {/* Turno Tarde */}
                            <div className="grid grid-cols-2 gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                                <div className="col-span-2 text-xs font-bold text-indigo-400/80 mb-1 flex items-center gap-1">
                                    <Moon className="w-3 h-3" /> Turno Tarde
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Hora inicio</label>
                                    <input
                                        type="time"
                                        value={servidorForm.hora_inicio2}
                                        onChange={(e) => setServidorForm(prev => ({ ...prev, hora_inicio2: e.target.value }))}
                                        className="w-full px-3 py-1.5 rounded bg-black/20 border border-white/10 text-white focus:outline-none focus:border-cyan-500 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Hora fin</label>
                                    <input
                                        type="time"
                                        value={servidorForm.hora_fin2}
                                        onChange={(e) => setServidorForm(prev => ({ ...prev, hora_fin2: e.target.value }))}
                                        className="w-full px-3 py-1.5 rounded bg-black/20 border border-white/10 text-white focus:outline-none focus:border-cyan-500 text-sm"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-2 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> Tipo de dia
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {TIPO_DIA_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setServidorForm(prev => ({ ...prev, tipo_dia: opt.value }))}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${servidorForm.tipo_dia === opt.value
                                                ? `${opt.color} border-current`
                                                : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                                                }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Reparto checkbox */}
                            <button
                                type="button"
                                onClick={() => setServidorForm(prev => ({ ...prev, reparto: !prev.reparto }))}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${servidorForm.reparto
                                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                    }`}
                            >
                                <Truck className="w-5 h-5" />
                                <span className="flex-1 text-left font-medium">Reparte (no recoge en terminal)</span>
                                {servidorForm.reparto && (
                                    <div className="w-5 h-5 rounded bg-orange-500 flex items-center justify-center">
                                        <span className="text-white text-xs">✓</span>
                                    </div>
                                )}
                            </button>

                            {/* Primas por turno */}
                            {primas.length > 0 && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-2 flex items-center gap-1">
                                        <Award className="w-3 h-3" /> Primas por turno
                                    </label>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Selecciona en que turnos aplica cada prima (M=Mañana, T=Tarde, Ambos=sin turno)
                                    </p>
                                    <div className="space-y-2">
                                        {primas.map(prima => (
                                            <div key={prima.id} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                                                <span className="flex-1 text-sm text-white">{prima.nombre}</span>
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePrimaConfig(prima.id, 'M')}
                                                        className={`px-2 py-1 text-xs rounded transition-all ${isPrimaSelected(prima.id, 'M')
                                                            ? 'bg-yellow-500/30 text-yellow-400 border border-yellow-500/50'
                                                            : 'bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10'
                                                            }`}
                                                        title="Solo mañana"
                                                    >
                                                        <Sun className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePrimaConfig(prima.id, 'T')}
                                                        className={`px-2 py-1 text-xs rounded transition-all ${isPrimaSelected(prima.id, 'T')
                                                            ? 'bg-indigo-500/30 text-indigo-400 border border-indigo-500/50'
                                                            : 'bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10'
                                                            }`}
                                                        title="Solo tarde"
                                                    >
                                                        <Moon className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePrimaConfig(prima.id, 'A')}
                                                        className={`px-2 py-1 text-xs rounded transition-all ${isPrimaSelected(prima.id, 'A')
                                                            ? 'bg-green-500/30 text-green-400 border border-green-500/50'
                                                            : 'bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10'
                                                            }`}
                                                        title="Ambos turnos"
                                                    >
                                                        Ambos
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowServidorModal(false)}
                                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={saveServidor}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-green-500 text-gray-900 font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Creation Modal */}
            {showBulkModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-purple-500/20 w-full max-w-lg p-6">
                        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                            <Zap className="w-5 h-5 text-purple-400" />
                            Creación Masiva de Servidores
                        </h2>
                        <p className="text-gray-400 text-sm mb-4">
                            Crea múltiples servidores a la vez para una o más líneas
                        </p>

                        <div className="space-y-4">
                            {/* Line selection */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm text-gray-400">Seleccionar líneas</label>
                                    <button
                                        onClick={selectAllLineas}
                                        className="text-xs text-purple-400 hover:text-purple-300"
                                    >
                                        {bulkForm.linea_ids.length === lineas.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {lineas.map(linea => (
                                        <button
                                            key={linea.id}
                                            onClick={() => toggleLineaSelection(linea.id)}
                                            className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${bulkForm.linea_ids.includes(linea.id)
                                                ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                                }`}
                                        >
                                            {bulkForm.linea_ids.includes(linea.id) && (
                                                <Check className="w-4 h-4" />
                                            )}
                                            <span className="font-mono font-bold">{linea.codigo}</span>
                                            <span className="text-xs opacity-70">{linea.nombre}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Day type */}
                            <div>
                                <label className="block text-sm text-gray-400 mb-2 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> Tipo de día
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {TIPO_DIA_OPTIONS.filter(opt => opt.value !== 'todos').map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setBulkForm(prev => ({ ...prev, tipo_dia: opt.value }))}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${bulkForm.tipo_dia === opt.value
                                                ? `${opt.color} border-current`
                                                : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                                                }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Quantity */}
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Cantidad de servidores</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={bulkForm.cantidad}
                                    onChange={(e) => setBulkForm(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Se crearán servidores numerados del 1 al {bulkForm.cantidad}
                                </p>
                            </div>

                            {/* Time ranges */}
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Incio Mañana</label>
                                        <input
                                            type="time"
                                            value={bulkForm.hora_inicio1}
                                            onChange={(e) => setBulkForm(prev => ({ ...prev, hora_inicio1: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Fin Mañana</label>
                                        <input
                                            type="time"
                                            value={bulkForm.hora_fin1}
                                            onChange={(e) => setBulkForm(prev => ({ ...prev, hora_fin1: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500 text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Inicio Tarde</label>
                                        <input
                                            type="time"
                                            value={bulkForm.hora_inicio2}
                                            onChange={(e) => setBulkForm(prev => ({ ...prev, hora_inicio2: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Fin Tarde</label>
                                        <input
                                            type="time"
                                            value={bulkForm.hora_fin2}
                                            onChange={(e) => setBulkForm(prev => ({ ...prev, hora_fin2: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500 text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Summary */}
                            {bulkForm.linea_ids.length > 0 && (
                                <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                                    <p className="text-sm text-purple-300">
                                        <strong>Resumen:</strong> Se crearán <strong>{bulkForm.cantidad}</strong> servidores
                                        ({bulkForm.tipo_dia}) en <strong>{bulkForm.linea_ids.length}</strong> línea(s).
                                        <br />
                                        <span className="text-xs text-gray-400">
                                            Total: {bulkForm.cantidad * bulkForm.linea_ids.length} servidores
                                        </span>
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowBulkModal(false)}
                                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={saveBulk}
                                disabled={saving || bulkForm.linea_ids.length === 0}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                                Crear {bulkForm.cantidad * bulkForm.linea_ids.length} Servidores
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, type: null, id: null, lineaId: null })}
                onConfirm={handleConfirmDelete}
                title={confirmModal.type === 'linea' ? 'Eliminar Línea' : 'Eliminar Servidor'}
                message={confirmModal.type === 'linea'
                    ? '¿Eliminar esta línea y todos sus servidores?\nEsta acción no se puede deshacer.'
                    : '¿Eliminar este servidor?\nEsta acción no se puede deshacer.'
                }
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
}
